import type { Engine } from '@babylonjs/core/Engines/engine';
import type { AdaptiveQualityState } from './AdaptiveQuality';
import type { RuntimeProfile } from './RuntimeProfile';

interface DiagnosticsSnapshot {
  fps: number;
  pixelRatio: number;
  hardwareScaling: number;
  viewport: string;
  canvas: string;
  mobile: boolean;
  ios: boolean;
  renderer: string;
  cameraPos: string;
  cameraTarget: string;
  cameraFov: number;
}

function getRenderer(canvas: HTMLCanvasElement): string {
  const gl = canvas.getContext('webgl2') || canvas.getContext('webgl');
  if (!gl) return 'unavailable';

  const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
  if (!debugInfo) return 'masked';

  return String(gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || 'unknown');
}

function createOverlay(): HTMLDivElement {
  const el = document.createElement('div');
  el.id = 'mobile-diagnostics';
  el.style.cssText = [
    'position:fixed',
    'left:8px',
    'bottom:8px',
    'z-index:99999',
    'max-width:min(360px,calc(100vw - 16px))',
    'padding:8px 10px',
    'border:1px solid rgba(255,255,255,0.22)',
    'border-radius:8px',
    'background:rgba(0,0,0,0.72)',
    'color:#fff',
    'font:11px/1.35 ui-monospace,SFMono-Regular,Menlo,Consolas,monospace',
    'white-space:pre-wrap',
    'pointer-events:none',
    'text-shadow:0 1px 1px rgba(0,0,0,0.6)',
  ].join(';');
  document.body.appendChild(el);
  return el;
}

function snapshot(
  engine: Engine,
  canvas: HTMLCanvasElement,
  profile: RuntimeProfile,
  adaptive: AdaptiveQualityState,
  renderer: string,
): DiagnosticsSnapshot {
  const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
  const activeCamera = (engine as any).scenes?.[0]?.activeCamera;
  const cameraPos = activeCamera?.position
    ? activeCamera.position.asArray().map((v: number) => v.toFixed(1)).join(',')
    : 'n/a';
  const cameraTarget = activeCamera?.getTarget
    ? activeCamera.getTarget().asArray().map((v: number) => v.toFixed(1)).join(',')
    : 'n/a';
  return {
    fps: Math.round(engine.getFps()),
    pixelRatio: adaptive.pixelRatio,
    hardwareScaling: engine.getHardwareScalingLevel(),
    viewport: `${window.innerWidth}x${Math.round(viewportHeight)}`,
    canvas: `${canvas.width}x${canvas.height}`,
    mobile: profile.isMobile,
    ios: profile.isIOS,
    renderer,
    cameraPos,
    cameraTarget,
    cameraFov: activeCamera?.fov ? Math.round(activeCamera.fov * 100) / 100 : 0,
  };
}

export function installMobileDiagnostics(
  engine: Engine,
  canvas: HTMLCanvasElement,
  profile: RuntimeProfile,
  adaptive: AdaptiveQualityState,
): void {
  const params = new URLSearchParams(window.location.search);
  if (!params.has('debug')) return;

  const renderer = getRenderer(canvas);
  const overlay = createOverlay();

  const update = () => {
    const stats = snapshot(engine, canvas, profile, adaptive, renderer);
    (window as any).__eldrickMobileDiagnostics = stats;
    overlay.textContent = [
      `fps ${stats.fps}`,
      `dpr ${stats.pixelRatio.toFixed(2)} scale ${stats.hardwareScaling.toFixed(2)}`,
      `viewport ${stats.viewport} canvas ${stats.canvas}`,
      `cam pos ${stats.cameraPos}`,
      `cam tgt ${stats.cameraTarget} fov ${stats.cameraFov.toFixed(2)}`,
      `mobile ${stats.mobile ? 'yes' : 'no'} ios ${stats.ios ? 'yes' : 'no'}`,
      `gpu ${stats.renderer}`,
    ].join('\n');
  };

  update();
  window.setInterval(update, 1000);
}
